/** Shared bits for the department inboxes (Collections lead feedback, 9 Oct 2026):
 *  SignoffHint — shows how the email will be signed; AssignMenu — assign an email to any collector; MoveDoneDialog — asks what to do
 *  when "Move to done" is pressed on an email that isn't logged anywhere. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, FolderCheck, FolderInput, Loader2, PenLine, UserCheck, UserPlus, UserX } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { getMySignoff } from "@/lib/api";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

let _signoffCache = null;
export const invalidateSignoffCache = () => { _signoffCache = null; };

export function SignoffHint() {
  const [s, setS] = useState(_signoffCache);
  useEffect(() => { if (!_signoffCache) getMySignoff().then((r) => { _signoffCache = r; setS(r); }).catch(() => {}); }, []);
  if (!s) return null;
  const txt = s.preview?.text ? s.preview.text.replace(/\n+/g, " · ") : "no personal sign-off (switched off)";
  return (
    <div className="text-[10px] text-muted-foreground flex items-center gap-1.5 flex-wrap" data-testid="signoff-hint">
      <PenLine className="w-3 h-3 shrink-0" />
      <span className="truncate">Signed off as: <span className="text-slate-700">{txt}</span> — the company signature is added by CodeTwo.</span>
      <span aria-hidden="true">·</span>
      <Link to="/profile" className="underline shrink-0" target="_blank" rel="noreferrer" data-testid="signoff-edit-link">Edit sign-off</Link>
    </div>
  );
}

export function AssignMenu({ email, api, me, onAssigned }) {
  const [people, setPeople] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => { if (!people) api.assignees().then((r) => setPeople(r.people || [])).catch(() => setPeople([])); };
  const set = async (name, mail) => {
    setBusy(true);
    try {
      const r = await api.assign(email.id, name, mail);
      onAssigned(r.assigned);
      toast.success(name ? (name === me?.name ? "Assigned to you" : `Assigned to ${name}`) : "Unassigned");
    } catch (e) { toast.error(errorDetailMessage(e, e.message)); } finally { setBusy(false); }
  };
  const mine = email.assigned && me && (email.assigned.email === (me.email || "").toLowerCase() || email.assigned.name === me.name);
  return (
    <DropdownMenu onOpenChange={(o) => o && load()}>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" disabled={busy}
          className={cn("h-7 text-[11px]", email.assigned ? (mine ? "text-sky-700 border-sky-300 bg-sky-50 hover:bg-sky-100" : "text-indigo-700 border-indigo-300 bg-indigo-50 hover:bg-indigo-100") : "text-slate-600")}
          title={email.assigned ? `Assigned to ${email.assigned.name} — click to reassign` : "Assign this email to a collector so nobody double-handles it"}
          data-testid={email.assigned ? "assigned-chip-btn" : "assign-btn"}>
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : email.assigned ? <UserCheck className="w-3 h-3 mr-1" /> : <UserPlus className="w-3 h-3 mr-1" />}
          <span className="truncate max-w-[160px]">{email.assigned ? email.assigned.name : "Assign"}</span><ChevronDown className="w-3 h-3 ml-1 opacity-60 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[320px] overflow-auto min-w-[220px]" data-testid="assign-menu">
        <DropdownMenuItem onClick={() => set(me?.name || me?.email || "Me", me?.email || "")} className="text-xs font-medium" data-testid="assign-to-me">
          <UserPlus className="w-3.5 h-3.5 mr-2" />Assign to me
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {people === null && <div className="px-2 py-1.5 text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Loading…</div>}
        {[true, false].map((team) => {
          const rows = (people || []).filter((p) => p.name !== (me?.name || "") && !!p.team === team);
          if (!rows.length) return null;
          return (
            <div key={String(team)}>
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">{team ? (api.dept === "legal" ? "Legal team" : "Collections team") : "Everyone else"}</DropdownMenuLabel>
              {rows.map((p) => (
                <DropdownMenuItem key={p.email || p.name} onClick={() => set(p.name, p.email)} className="text-xs" data-testid={`assign-to-${p.name.replace(/\s+/g, "-").toLowerCase()}`}>
                  {p.name}{email.assigned?.name === p.name && <span className="ml-auto text-[10px] text-muted-foreground">current</span>}
                </DropdownMenuItem>
              ))}
            </div>
          );
        })}
        {email.assigned && (<><DropdownMenuSeparator /><DropdownMenuItem onClick={() => set("", "")} className="text-xs text-rose-700" data-testid="assign-unassign"><UserX className="w-3.5 h-3.5 mr-2" />Unassign</DropdownMenuItem></>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function MoveDoneDialog({ email, onPickFile, onMoveAnyway, onClose }) {
  const ld = email.triage?.matched_case?.kind === "loandisk" ? email.triage.matched_case : null;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="move-done-dialog">
        <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><FolderCheck className="w-4 h-4 text-slate-600" />Not logged on any customer file</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">
          <strong className="text-slate-800">{email.subject || "(no subject)"}</strong> hasn't been logged on a customer file
          {ld ? <> — the customer is in LoanDisk ({ld.sf || ld.customer_name}) but has no portal file yet. Use <strong className="text-slate-800">Add account &amp; log</strong> in the reading pane to open one, pick another file, or move anyway.</>
            : <> and the AI didn't match one. Moving it to Done leaves no trace on any account.</>}
        </p>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="move-done-cancel">Cancel</Button>
          <Button variant="outline" onClick={onMoveAnyway} data-testid="move-done-anyway"><FolderCheck className="w-3.5 h-3.5 mr-1" />Move anyway</Button>
          <Button onClick={onPickFile} data-testid="move-done-pick-file"><FolderInput className="w-3.5 h-3.5 mr-1" />Pick a file &amp; log it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
